/**
 * Which tab owns a path, and how a link to somebody else's tab is handed over.
 *
 * The installed application is a real tab bar with a separate web view behind each tab.
 * That is what lets a teacher open a session from Schedule, glance at the map, and come
 * back to the session still sitting there — each tab keeps its own place because each tab
 * is its own page.
 *
 * It also means a link can put the wrong section inside a tab. The room directory lives
 * under More and every room links to `/map?room=…`, so tapping one left the More tab
 * showing the Map while the bar underneath still said More. Nathan reported exactly that.
 * The same link is on every session page, so Schedule and My WVCEA could show the Map too.
 *
 * The rule, the same one the shells enforce when a tab comes forward: a cross-tab link
 * opens in the tab that owns it, and the tab it was tapped from is left exactly where it
 * was. Tap More afterwards and the room directory is still there.
 *
 * **Why the web has to be the one to notice.** The obvious place for this is the shell —
 * cancel the navigation and route it. But `Link` calls `preventDefault` and then
 * `pushState`, so no request ever leaves the page: `decidePolicyFor` on iOS and
 * `shouldOverrideUrlLoading` on Android never fire, and there is nothing for either shell
 * to cancel. The page is the only side that knows the move is happening, so the page is
 * the side that says so.
 *
 * In an ordinary browser and in the installed web app, none of this runs. There is one
 * page and one history there, and the bottom navigation the web draws for itself already
 * highlights correctly.
 */

/** The five tabs the shells draw, in the order they draw them. */
export const SHELL_TABS = ["/", "/schedule", "/my-wvcea", "/map", "/more"] as const;

export type ShellTab = (typeof SHELL_TABS)[number];

/**
 * The tab a path belongs to.
 *
 * Kept in step with `Tab.matching` in `apps/ios/Sources/Tabs.swift` and
 * `Tab.matching` in `apps/android/.../Tab.kt`. The three are checked against each other by
 * the native v1 contract, because a route added to two lists out of three lands in the
 * wrong tab on one platform and nobody notices until somebody is holding that phone.
 *
 * `/my-stuff` is deliberately absent, as it is on iOS. Android answers to it and the
 * contract pins that difference; closing it is its own decision.
 */
export function tabOwning(path: string): ShellTab {
  if (path === "/") return "/";
  if (path.startsWith("/schedule")) return "/schedule";
  if (path.startsWith("/my-wvcea") || path.startsWith("/my-convention")) return "/my-wvcea";
  if (path.startsWith("/map") || path.startsWith("/explore")) return "/map";
  if (path.startsWith("/more") || path.startsWith("/updates") || path.startsWith("/help")) return "/more";
  return "/";
}

/** The path part of an href, without the query the tab rule does not read. */
function pathOf(href: string): string {
  const cut = href.search(/[?#]/u);
  return cut === -1 ? href : href.slice(0, cut);
}

/** Which shell is showing this page, or null in an ordinary browser. */
export function shellPlatform(): "ios" | "android" | null {
  if (typeof document === "undefined") return null;
  const mark = document.documentElement.getAttribute("data-shell");
  return mark === "ios" || mark === "android" ? mark : null;
}

interface ShellHost {
  webkit?: { messageHandlers?: { VerityConnectRoute?: { postMessage(value: string): void } } };
  VerityConnectRoute?: { open(path: string): void };
}

/**
 * Hand a cross-tab link to the shell, and say whether it was taken.
 *
 * False means carry on and navigate normally, which covers every case that is not a shell:
 * a browser, the installed web app, a link inside the tab it was tapped from, and an
 * address the rule does not recognise.
 *
 * True means the shell has it. The caller must not call `pushState`, because the whole
 * point is that this tab does not move.
 *
 * `platform` and `host` are arguments rather than reads so this can be tested without a
 * browser. The whole suite runs under node, and a rule this easy to get wrong is worth
 * more than the one line it saves.
 */
export function handOverToShell(
  href: string,
  currentPath: string,
  platform: "ios" | "android" | null = shellPlatform(),
  host: ShellHost = globalThis as ShellHost,
): boolean {
  if (!platform) return false;
  // Absolute addresses and anything off-site belong to the browser, not to a tab.
  // `//host/path` reads as a path right up until a URL parser sees it.
  if (!href.startsWith("/") || href.startsWith("//")) return false;
  if (tabOwning(pathOf(href)) === tabOwning(currentPath)) return false;

  const handler = host.webkit?.messageHandlers?.VerityConnectRoute;
  if (handler) { handler.postMessage(href); return true; }
  if (host.VerityConnectRoute) { host.VerityConnectRoute.open(href); return true; }
  // A shell too old to carry the bridge. Navigating in place is what it did before this
  // existed, which is wrong in a small way; refusing to move would be wrong in a large
  // one.
  return false;
}
